import React, { useState } from 'react';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { decimalToAmerican, formatPayout, BET_LIMITS, formatPropSelection } from '../../constants/sports';
import { useToast } from '../ToastContext';

function BetSlip({ legs, userData, onRemoveLeg, onClear, onBetPlaced, onClose, isMobile }) {
  const toast = useToast();
  const [betMode, setBetMode] = useState(legs.length > 1 ? 'parlay' : 'straight');
  const [wager, setWager] = useState('');
  const [currency, setCurrency] = useState('crewmate');
  const [placing, setPlacing] = useState(false);

  const useImpostor = currency === 'impostor';
  const balance = useImpostor ? (userData?.imposterTokens || 0) : (userData?.crewmateTokens || 0);
  const wagerNum = parseFloat(wager) || 0;

  // Auto-switch to parlay if multiple legs
  const effectiveMode = legs.length > 1 ? betMode : 'straight';

  // Calculate odds
  const combinedOdds = legs.reduce((acc, l) => acc * l.odds, 1);
  const straightPayouts = legs.map(l => wagerNum * l.odds);
  const parlayPayout = wagerNum * combinedOdds;
  const totalPayout = effectiveMode === 'parlay' ? parlayPayout : straightPayouts.reduce((a, b) => a + b, 0);
  const totalWager = effectiveMode === 'parlay' ? wagerNum : wagerNum * legs.length;

  const canPlace = wagerNum >= BET_LIMITS.MIN_BET && totalWager <= balance && legs.length > 0 && !placing;

  const handlePlace = async () => {
    if (!canPlace) return;
    setPlacing(true);

    try {
      if (effectiveMode === 'parlay') {
        const fn = httpsCallable(functions, 'placeSportsBet');
        const result = await fn({
          legs: legs.map(l => {
            const base = { gameId: l.gameId, sport: l.sport, market: l.market, selection: l.selection, point: l.point };
            if (l.market === 'player_prop') {
              base.athleteId = l.athleteId;
              base.propType = l.propType;
            }
            return base;
          }),
          currency,
          wagerTokens: wagerNum,
        });
        toast.success(`Parlay placed! Potential payout: ${formatPayout(result.data.potentialPayout)}`);
      } else {
        // Place each leg as a separate straight bet
        const fn = httpsCallable(functions, 'placeSportsBet');
        for (const leg of legs) {
          const base = { gameId: leg.gameId, sport: leg.sport, market: leg.market, selection: leg.selection, point: leg.point };
          if (leg.market === 'player_prop') {
            base.athleteId = leg.athleteId;
            base.propType = leg.propType;
          }
          await fn({
            legs: [base],
            currency,
            wagerTokens: wagerNum,
          });
        }
        toast.success(`${legs.length} straight bet(s) placed!`);
      }
      setWager('');
      onBetPlaced();
    } catch (err) {
      toast.error(err.message || 'Failed to place bet');
    }
    setPlacing(false);
  };

  const formatSelection = (leg) => {
    if (leg.market === 'player_prop') return formatPropSelection(leg);
    if (leg.market === 'h2h') {
      if (leg.selection === 'home') return leg.homeTeam;
      if (leg.selection === 'away') return leg.awayTeam;
      return 'Draw';
    }
    if (leg.market === 'spreads') {
      const team = leg.selection === 'home' ? leg.homeTeam : leg.awayTeam;
      return `${team} ${leg.point > 0 ? '+' : ''}${leg.point}`;
    }
    if (leg.market === 'totals') {
      return `${leg.selection === 'over' ? 'Over' : 'Under'} ${leg.point}`;
    }
    return leg.selection;
  };

  const formatMarketLabel = (market) => {
    if (market === 'h2h') return 'Moneyline';
    if (market === 'spreads') return 'Spread';
    if (market === 'totals') return 'Total';
    if (market === 'player_prop') return 'Player Prop';
    return market;
  };

  return (
    <div className="bet-slip">
      <div className="bet-slip-header">
        <h3>Bet Slip ({legs.length})</h3>
        <div className="bet-slip-header-actions">
          <button className="bet-slip-clear" onClick={onClear}>Clear</button>
          {onClose && <button className="bet-slip-close" onClick={onClose}>×</button>}
        </div>
      </div>

      <div className="bet-slip-legs">
        {legs.map((leg, i) => (
          <div key={`${leg.gameId}-${leg.market}-${leg.selection}`} className="bet-slip-leg">
            <div className="bet-slip-leg-info">
              <div className="bet-slip-leg-selection">{formatSelection(leg)}</div>
              <div className="bet-slip-leg-details">
                <span className="bet-slip-leg-market">{formatMarketLabel(leg.market)}</span>
                <span className="bet-slip-leg-matchup">{leg.awayTeam} @ {leg.homeTeam}</span>
              </div>
              <div className="bet-slip-leg-odds">{decimalToAmerican(leg.odds)}</div>
            </div>
            <button className="bet-slip-leg-remove" onClick={() => onRemoveLeg(i)}>×</button>
          </div>
        ))}
      </div>

      {legs.length > 1 && (
        <div className="bet-slip-mode">
          <button className={`bet-mode-btn ${effectiveMode === 'straight' ? 'active' : ''}`} onClick={() => setBetMode('straight')}>
            Straight ({legs.length})
          </button>
          <button className={`bet-mode-btn ${effectiveMode === 'parlay' ? 'active' : ''}`} onClick={() => setBetMode('parlay')}>
            Parlay
          </button>
        </div>
      )}

      <div className="bet-slip-wager">
        <div className="bet-slip-currency">
          <button className={`currency-btn ${currency === 'crewmate' ? 'active' : ''}`} onClick={() => setCurrency('crewmate')}>
            🔵 Crew
          </button>
          <button className={`currency-btn ${currency === 'impostor' ? 'active' : ''}`} onClick={() => setCurrency('impostor')}>
            🔴 Imp
          </button>
          <span className="bet-slip-balance">Bal: {balance.toFixed(2)}</span>
        </div>

        <div className="bet-slip-input-row">
          <span className="bet-slip-input-label">{effectiveMode === 'parlay' ? 'Wager' : 'Per bet'}</span>
          <input
            type="number"
            className="bet-slip-input"
            placeholder={`Min ${BET_LIMITS.MIN_BET}`}
            value={wager}
            onChange={(e) => setWager(e.target.value)}
            min={BET_LIMITS.MIN_BET}
            step="0.5"
          />
        </div>

        {wagerNum > 0 && (
          <div className="bet-slip-summary">
            <div className="bet-slip-summary-row">
              <span>Total wager</span>
              <span>{useImpostor ? '🔴' : '🔵'} {totalWager.toFixed(2)}</span>
            </div>
            {effectiveMode === 'parlay' && (
              <div className="bet-slip-summary-row">
                <span>Combined odds</span>
                <span>{decimalToAmerican(combinedOdds)}</span>
              </div>
            )}
            <div className="bet-slip-summary-row bet-slip-payout">
              <span>Potential payout</span>
              <span>{useImpostor ? '🔴' : '🔵'} {formatPayout(totalPayout)}</span>
            </div>
          </div>
        )}
      </div>

      <button
        className="bet-slip-place-btn"
        onClick={handlePlace}
        disabled={!canPlace}
      >
        {placing ? 'Placing...' : totalWager > balance ? 'Insufficient balance' : `Place ${effectiveMode === 'parlay' ? 'Parlay' : `${legs.length} Bet${legs.length > 1 ? 's' : ''}`}`}
      </button>
    </div>
  );
}

export default BetSlip;
